import React, { useState, useEffect } from 'react';
import { LLMModelInfo, VideoModel, ImageModel, ServicePricing } from '../types';
import { listLLMProvidersPublic, listActiveVideoModels, listActiveImageModels, listServicePricingPublic } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import { useTranslation } from 'react-i18next';

interface LLMRow {
  provider: string;
  displayName: string;
  model: string;
  inputCredits: number;
  outputCredits: number;
}

const Pricing: React.FC = () => {
  const { t } = useTranslation('profile');
  const [llmRows, setLlmRows] = useState<LLMRow[]>([]);
  const [videoModels, setVideoModels] = useState<VideoModel[]>([]);
  const [imageModels, setImageModels] = useState<ImageModel[]>([]);
  const [servicePricing, setServicePricing] = useState<ServicePricing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      listLLMProvidersPublic().then(providers => {
        const rows: LLMRow[] = [];
        for (const p of providers) {
          if (p.disabled) continue;
          try {
            const models: LLMModelInfo[] = JSON.parse(p.models || '[]');
            for (const m of models) {
              if (m.disabled) continue;
              rows.push({
                provider: p.name,
                displayName: m.displayName || m.name,
                model: m.name,
                inputCredits: m.inputCredits ?? 0,
                outputCredits: m.outputCredits ?? 0,
              });
            }
          } catch {}
        }
        setLlmRows(rows);
      }).catch(() => {}),
      listActiveVideoModels().then(setVideoModels).catch(() => {}),
      listActiveImageModels().then(setImageModels).catch(() => {}),
      listServicePricingPublic().then(setServicePricing).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const containerStyle = { maxWidth: '960px', margin: '0 auto' } as const;

  if (loading) {
    return (
      <div className="container" style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('pricing.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container" style={containerStyle}>
      <Breadcrumb items={[
        { label: t('pricing.breadcrumbProjects'), to: '/projects' },
        { label: t('pricing.breadcrumbTitle') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('pricing.pageTitle')}</h1>
      </div>

      {/* Overview cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <SummaryCard label={t("pricing.llmModels")} count={llmRows.length} unit={t("pricing.modelUnit")} color="var(--primary)" />
        <SummaryCard label={t("pricing.imageGeneration")} count={imageModels.length} unit={t("pricing.modelUnit")} color="var(--info)" />
        <SummaryCard label={t("pricing.videoGeneration")} count={videoModels.length} unit={t("pricing.modelUnit")} color="var(--warning)" />
        <div className="card" style={{ textAlign: 'center', padding: '1rem 0.75rem', background: 'var(--surface-2, #f9fafb)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginBottom: '0.25rem' }}>{t('pricing.exchangeRate')}</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)' }}>{t('pricing.currencyUnit')}</div>
        </div>
      </div>

      {/* LLM Models */}
      <Section title={t("pricing.llmModels")} subtitle={t("pricing.llmSubtitle")} color="var(--primary)">
        {llmRows.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={thStyle}>{t('pricing.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right'}}>{t('pricing.thInputPrice')}</th>
                  <th style={{ ...thStyle, textAlign: 'right'}}>{t('pricing.thOutputPrice')}</th>
                </tr>
              </thead>
              <tbody>
                {llmRows.map((row, i) => (
                  <tr key={row.model} style={{ ...tbodyRowStyle, background: i % 2 === 0 ? 'transparent' : 'var(--surface-2, rgba(0,0,0,0.015))' }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{row.displayName}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><CreditValue value={row.inputCredits * 1000} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><CreditValue value={row.outputCredits * 1000} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#129302;" description={t("pricing.emptyLLM")} />
        )}
      </Section>

      {/* Image Models */}
      <Section title={t("pricing.imageGeneration")} subtitle={t("pricing.imageSubtitle")} color="var(--info)">
        {imageModels.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={thStyle}>{t('pricing.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right'}}>{t('pricing.thPricePerCall')}</th>
                </tr>
              </thead>
              <tbody>
                {imageModels.map((m, i) => (
                  <tr key={m.id} style={{ ...tbodyRowStyle, background: i % 2 === 0 ? 'transparent' : 'var(--surface-2, rgba(0,0,0,0.015))' }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{m.displayName || m.model}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><CreditValue value={m.creditsPerCall} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#127912;" description={t("pricing.emptyImage")} />
        )}
      </Section>

      {/* Video Models */}
      <Section title={t("pricing.videoGeneration")} subtitle={t("pricing.videoSubtitle")} color="var(--warning)">
        {videoModels.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={thStyle}>{t('pricing.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right'}}>{t('pricing.thImageInput')}</th>
                  <th style={{ ...thStyle, textAlign: 'right'}}>{t('pricing.thVideoInput')}</th>
                </tr>
              </thead>
              <tbody>
                {videoModels.map((m, i) => (
                  <tr key={m.id} style={{ ...tbodyRowStyle, background: i % 2 === 0 ? 'transparent' : 'var(--surface-2, rgba(0,0,0,0.015))' }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{m.displayName || m.model}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><CreditValue value={m.creditsPerSec} unit={t('pricing.pricePerSec')} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><CreditValue value={m.creditsPerSecWithVideo} unit={t('pricing.pricePerSec')} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#127909;" description={t("pricing.emptyVideo")} />
        )}
      </Section>

      {/* Other services */}
      <Section title={t("pricing.otherServices")} subtitle={t("pricing.otherServicesSubtitle")} color="var(--success)">
        {servicePricing.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={thStyle}>{t('pricing.thService')}</th>
                  <th style={thStyle}>{t('pricing.thDescription')}</th>
                  <th style={{ ...thStyle, textAlign: 'right'}}>{t('pricing.thPricePerUnit')}</th>
                </tr>
              </thead>
              <tbody>
                {servicePricing.map((sp, i) => (
                  <tr key={sp.serviceType} style={{ ...tbodyRowStyle, background: i % 2 === 0 ? 'transparent' : 'var(--surface-2, rgba(0,0,0,0.015))' }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{sp.displayName}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-3)', fontSize: '0.8rem' }}>{sp.description}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><CreditValue value={sp.creditsPerUnit} unit={t('pricing.thPricePerUnit')} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#9881;" description={t("pricing.emptyServices")} />
        )}
      </Section>
    </div>
  );
};

/* ─── Sub-components ─── */

function SummaryCard({ label, count, unit, color }: { label: string; count: number; unit: string; color: string }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '1rem 0.75rem', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{unit}</div>
    </div>
  );
}

function Section({ title, subtitle, color, children }: { title: string; subtitle: string; color: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ width: 4, height: '1.1rem', borderRadius: 2, background: color, flexShrink: 0, alignSelf: 'center' }} />
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{title}</h2>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>{subtitle}</span>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

function CreditValue({ value, unit }: { value: number; unit?: string }) {
  const { t } = useTranslation('profile');
  if (value === 0) {
    return (
      <span style={{
        display: 'inline-block',
        padding: '0.15rem 0.5rem',
        borderRadius: 10,
        fontSize: '0.78rem',
        fontWeight: 500,
        background: 'var(--success-light)',
        color: 'var(--success)',
      }}>
        {t('pricing.free')}
      </span>
    );
  }
  return (
    <span style={{ fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
      ¥{(value / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      {unit && <span style={{ fontWeight: 400, color: 'var(--text-3)', fontSize: '0.75rem', marginLeft: '0.25rem' }}>{unit}</span>}
    </span>
  );
}

/* ─── Styles ─── */

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const theadRowStyle: React.CSSProperties = {
  borderBottom: '2px solid var(--border)',
  background: 'var(--surface-2, rgba(0,0,0,0.02))',
};

const tbodyRowStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border)',
  transition: 'background 0.15s',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.65rem 0.85rem',
  fontWeight: 600,
  fontSize: '0.78rem',
  color: 'var(--text-3)',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

const tdStyle: React.CSSProperties = {
  padding: '0.7rem 0.85rem',
  whiteSpace: 'nowrap',
};

export default Pricing;
